import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { Label } from '@drone-js/date';
import { formatPriceWithAbbreviation } from 'component/page/detail/detailUtils';
import { OfferLevelSchedules } from 'component/page/detail/store/calendar.types';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { ActivityAvailabilityStatus } from 'gql/autogen/ActivityAvailabilityStatus';
import { parseISO } from 'date-fns';

export const mapPriceLabels = (schedules: AvailabilitySchedule[], locale: string): Label[] => {
    return schedules
        .filter((item) => item.isAvailable)
        .map((item): Label => {
            return {
                date: parseISO(item.date ?? ''),
                label: item.price ? formatPriceWithAbbreviation(item.price, locale) : '',
            };
        });
};

export const filterUnavailableDates = (schedules: AvailabilitySchedule[]): Date[] => {
    return schedules.filter((schedule) => !schedule.isAvailable).map((schedule) => parseISO(schedule.date || '')) || [];
};

export const getPossibleOffersSchedules = (possibleOfferIds: (number | undefined)[], offerCalendars: OfferLevelSchedules | undefined, schedule: AvailabilitySchedule) => {
    return possibleOfferIds
        .map((offerId) => {
            if (!offerId || !schedule.date) return undefined;
            return offerCalendars?.[offerId]?.[schedule.date];
        })
        .filter((offer) => offer && offer.isAvailable);
};

export const getCheapestOffer = (allPossibleSchedules: (AvailabilitySchedule | undefined)[]) => {
    return allPossibleSchedules.reduce((prev, current) => {
        if (!prev) return current;
        return (prev?.price || 0) < (current?.price || 0) ? prev : current;
    }, undefined);
};

/**
 * Get the cheapest schedules for the possible offers, based on the offer calendars and the availability schedules
 * @param possibleOffers
 * The possible offers for the selected offer option combination
 *
 * @param schedules
 * The availability schedules for the activity level, which will be the base for the cheapest schedules, as some offer calendars might not have availability for some dates
 *
 * @param offerCalendars
 * The offer level calendars for the possible offers
 */
export const getCheapestSchedules = (
    possibleOffers: EnrichedOfferOption[],
    schedules: AvailabilitySchedule[],
    offerCalendars: OfferLevelSchedules,
    basedCalendarOfferIds: (number | undefined)[],
) => {
    // when no possible offers are available, use the base calendar offer ids to display
    const possibleOfferIds = possibleOffers.length === 0 ? basedCalendarOfferIds : Array.from(new Set(possibleOffers?.map((offer) => offer.offerId) || []));
    const cheapestSchedules: AvailabilitySchedule[] = [];

    schedules.forEach((schedule) => {
        if (!schedule.isAvailable) {
            cheapestSchedules.push(schedule);
            return;
        }
        const allPossibleSchedules = getPossibleOffersSchedules(possibleOfferIds, offerCalendars, schedule);
        const cheapestOffer = getCheapestOffer(allPossibleSchedules);

        cheapestSchedules.push(
            cheapestOffer || {
                date: schedule.date,
                isAvailable: false,
                price: 0,
                status: ActivityAvailabilityStatus.UNAVAILABLE,
            },
        );
    });
    return cheapestSchedules;
};

interface NumberPriceCalendar {
    date: Date;
    price: number;
}

export const mapPriceNumbers = (schedules: AvailabilitySchedule[]): NumberPriceCalendar[] => {
    return schedules
        .filter((item) => item.isAvailable && item.date)
        .map((item): NumberPriceCalendar => {
            return {
                date: parseISO(item.date ?? ''),
                price: item.price || 0,
            };
        });
};
